import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/FeedbackPage.module.css';
import Toast, { ToastProps } from '../components/Toast';
import Footer from '../components/Footer';
import { SEO_METADATA } from '../config/seoMetadata';
import SEO from '../components/SEO';

interface FormData {
    feedback: string;
    email: string;
}

const FeedbackPage: React.FC = () => {
    // Add a ref to the main container for potential focus management
    const containerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Ensure we don't auto-focus any form inputs that might cause scrolling
        if (containerRef.current) {
            containerRef.current.focus({ preventScroll: true });
        }

        // Ensure scroll to top after any potential focus management
        const timer = setTimeout(() => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        }, 50);

        return () => clearTimeout(timer);
    }, []);

    const [formData, setFormData] = useState<FormData>({
        feedback: '',
        email: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastProps>({
        message: '',
        type: 'success',
        isVisible: false,
        onClose: () => {}
    });

    const showToast = useCallback((message: string, type: 'success' | 'error') => {
        setToast({
            message,
            type,
            isVisible: true,
            onClose: () => setToast(prev => ({ ...prev, isVisible: false }))
        });
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isSubmitting || !formData.feedback.trim()) {
            return;
        }

        const feedbackToSubmit = formData.feedback.trim();
        const emailToSubmit = formData.email.trim();

        // Start brewing animation
        setIsSubmitting(true);

        // Cohesive timing: after 600ms, clear form + show toast + stop brewing
        setTimeout(() => {
            setFormData({ feedback: '', email: '' });
            showToast('Thank you for your feedback! 🎵', 'success');
            setIsSubmitting(false);
        }, 600);

        // Submit to Google Sheets in background (doesn't affect UI)
        try {
            const scriptURL = 'https://script.google.com/macros/s/AKfycbyjWW2C_p7PN0Gz8BoTfTppvTs3TZTchYddyc2nu3scorohqm0zBwS0R1H0ZeVf9gV-/exec';
            const form = new FormData();
            form.append('feedback', feedbackToSubmit);
            if (emailToSubmit) {
                form.append('email', emailToSubmit);
            }

  
            const response = await fetch(scriptURL, {
                method: 'POST',
                body: form
            });

            const responseText = await response.text();

            // Parse JSON response
            let jsonResponse;
            try {
                jsonResponse = JSON.parse(responseText);

                if (jsonResponse.result === 'error') {
                    console.error('❌ Google Script Error:', jsonResponse.error);
                    throw new Error(`Script error: ${JSON.stringify(jsonResponse.error)}`);
                }
            } catch (parseError) {
                console.error('❌ Failed to parse response:', parseError);
            }

            if (!response.ok || jsonResponse?.result !== 'success') {
                throw new Error('Failed to submit');
            }

  
        } catch (error) {
            console.error('Feedback submission error:', error);
            // Silently fail - user already saw success. Could implement retry logic here.
        }
    }, [isSubmitting, formData, showToast]);

    const isFormValid = formData.feedback.trim().length > 0;

    return (
        <>
            <SEO {...SEO_METADATA.FEEDBACK} />
            <div className={styles.feedbackPage} ref={containerRef} tabIndex={-1}>
            <div className={styles.container}>
                {/* Header Section */}
                <div className={styles.header}>
                    <Link to="/" className={styles.backLink}>
                        <span className={styles.backIcon}>←</span>
                        <span>Back to Home</span>
                    </Link>

                    <div className={styles.titleSection}>
                        <h1 className={styles.title}>
                            <span className={styles.titleIcon}>💬</span>
                            Share Your Feedback
                        </h1>
                        <p className={styles.subtitle}>
                            Your thoughts help us make Audiated better for everyone! 🎵
                        </p>
                    </div>
                </div>

                {/* Info Cards Section */}
                <div className={styles.footerInfo}>
                    <div className={styles.infoCards}>
                        <div className={styles.infoCard}>
                            <span className={styles.infoIcon}>🎯</span>
                            <div className={styles.infoContent}>
                                <h3>Feature Requests</h3>
                                <p>Have an idea for a new feature? We'd love to hear it!</p>
                            </div>
                        </div>
                        <div className={styles.infoCard}>
                            <span className={styles.infoIcon}>🐛</span>
                            <div className={styles.infoContent}>
                                <h3>Bug Reports</h3>
                                <p>Found something not working as expected? Let us know!</p>
                            </div>
                        </div>
                        <div className={styles.infoCard}>
                            <span className={styles.infoIcon}>💡</span>
                            <div className={styles.infoContent}>
                                <h3>General Feedback</h3>
                                <p>Any thoughts on how we can improve your experience?</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className={styles.content}>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Feedback Input */}
                        <div className={styles.fieldGroup}>
                            <label htmlFor="feedback" className={styles.label}>
                                <span className={styles.labelIcon}>📝</span>
                                Your Feedback *
                            </label>
                            <textarea
                                id="feedback"
                                name="feedback"
                                value={formData.feedback}
                                onChange={handleInputChange}
                                placeholder="Share your thoughts, suggestions, or report issues. What would make Audiated better for you?"
                                className={styles.textarea}
                                required
                                rows={6}
                                aria-label="Your feedback"
                                aria-describedby="feedback-help"
                            />
                            <div className={styles.fieldHelper}>
                                <small id="feedback-help">
                                    Tell us what's on your mind - new features, improvements, or any issues you've encountered.
                                </small>
                            </div>
                        </div>

                        {/* Email Input (Optional) */}
                        <div className={styles.fieldGroup}>
                            <label htmlFor="email" className={styles.label}>
                                <span className={styles.labelIcon}>📧</span>
                                Email (Optional)
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="your.email@example.com"
                                className={styles.input}
                                aria-label="Your email address (optional)"
                                aria-describedby="email-help"
                            />
                            <small id="email-help" className={styles.fieldHelperText}>
                                Leave your email if you'd like us to follow up with you about your feedback.
                            </small>
                        </div>

                        {/* Submit Button */}
                        <div className={styles.submitSection}>
                            <button
                                type="submit"
                                disabled={!isFormValid || isSubmitting}
                                className={styles.submitButton}
                                aria-describedby="submit-status"
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className={styles.spinner}>🎵</span>
                                        Brewing...
                                    </>
                                ) : (
                                    <>
                                        <span className={styles.submitIcon}>🚀</span>
                                        Send Feedback
                                    </>
                                )}
                            </button>

                            {!isFormValid && (
                                <p className={styles.validationHint}>
                                    Please enter your feedback before submitting.
                                </p>
                            )}
                        </div>
                    </form>
                </div>

                {/* Toast Notification */}
                <Toast
                    message={toast.message}
                    type={toast.type}
                    isVisible={toast.isVisible}
                    onClose={toast.onClose}
                />
            </div>

            {/* Footer */}
            <Footer />
        </div>
        </>
    );
};

export default FeedbackPage;